import type { ComponentType } from 'react';
import { ShieldCheck, Lock, UsersRound, Share2, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface SecurityReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Name of the arche being reviewed (for the heading + per-arche note). */
  archeName: string;
  /** Arche-specific addendum to the shared explainer. */
  securityNote: string;
}

/** The shared Vault data-security points — the same for every arche. */
const VAULT_POINTS: { icon: ComponentType<{ className?: string }>; title: string; body: string }[] =
  [
    {
      icon: UsersRound,
      title: 'Your data is yours alone',
      body: 'Everything you create lives in your private Vault, scoped to you (or your organization). It is never mixed into a shared pool or used to train AI models.',
    },
    {
      icon: Lock,
      title: 'Encrypted and isolated',
      body: 'Documents and files are encrypted at rest in storage dedicated to your Vault. One tenant can never read another tenant’s data.',
    },
    {
      icon: Check,
      title: 'Every change is audited',
      body: 'Reads and writes flow through a single, audited pipeline. There are no side doors — each access is logged, so nothing happens to your data without a trail.',
    },
    {
      icon: Share2,
      title: 'You control sharing',
      body: 'Nothing leaves your Vault unless you explicitly grant access — document by document, person by person — and you can revoke it at any time.',
    },
  ];

/**
 * Explains why a user's data is safe in Fias. Shared Vault explainer plus a
 * short, arche-specific note. Opened from the "Security review" action on a
 * featured arche card.
 */
export function SecurityReviewDialog({
  open,
  onOpenChange,
  archeName,
  securityNote,
}: SecurityReviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Security review — {archeName}
          </DialogTitle>
          <DialogDescription>Why your data is safe when you keep it in Fias.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {VAULT_POINTS.map((point) => {
            const Icon = point.icon;
            return (
              <div key={point.title} className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{point.title}</p>
                  <p className="text-sm text-muted-foreground">{point.body}</p>
                </div>
              </div>
            );
          })}

          {/* Per-arche note */}
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
              For {archeName}
            </p>
            <p className="text-sm text-foreground">{securityNote}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
